import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    subProduct: { type: Object },
    subHandle: { type: String },
    selectedVariant: { type: Object },
    formId: { type: String },
    options: { type: Object },
    fallbackOptions: { type: Object },
    moneyFormat: { type: String },
    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },
    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },
    disableUrl: { type: Boolean },
    debug: { type: Boolean },
    dropdownOpen: { type: Boolean },
    customAdditionalDiscount: { type: Number },

    // Sold out feature
    soldOutData: { type: Object },
  }

  static styles = [
    css`
      :host {
        width: 100%;
        max-width: 440px;
      }

      .skio-plan-picker {
        display: flex;
        gap: 1rem;
        padding: 16px;
        background-color: #ECECEC;
        border: 0;
        border-radius: 12px;
        font-size: 15px;
        color: black;
        width: 100%;
        margin-bottom: 1rem;
        font-family: inherit;
        box-sizing: border-box;
      }

      .selling-plan-buttons > label[for="selling_plan_button-4706107644"] {
        display: none !important;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      :host([layout='vertical']) .skio-plan-picker {
        flex-direction: column;
      }

      :host([layout='horizontal']) .skio-plan-picker {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      .group-container {
        display: block;
        position: relative;
        transition: border-color 0.2s ease;
        border: var(--skio-group-border-width, 1px) solid var(--skio-group-border-color, #ccc);
        border-radius: var(--skio-group-border-radius, 0);
        background-color: var(--skio-group-background-color, transparent);
        color: var(--skio-group-text-color, #000);
      }

      .group-header {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 16px;
        text-transform: uppercase;
        font-weight: 400;
        height: 2.3em;
        color: #333333;
        font-family: 'ABC Monument Grotesk Mono' !important;
        background-color: var(--skio-group-border-color);
        display: none !important;
      }

      .group-container--selected .group-header {
        color: white;
      }

      .group-container--selected {
        border-color: var(--skio-group-border-selected-color, #333);
        background-color: var(--skio-group-background-selected-color, transparent);
        color: var(--skio-group-text-selected-color, #000);
      }

      .group-container--selected .group-header {
        background-color: var(--skio-group-border-selected-color);
      }

      .group-container--last {
        order: 1;
      }

      :host([layout='horizontal']) .group-container {
        width: calc(50% - 0.5rem - calc(2 * var(--skio-group-border-width, 1px)));
      }

      .group-input {
        position: absolute;
        opacity: 0;
        width: 0px;
        height: 0px;
      }

      .group-input:focus-visible ~ .group-label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-label {
        display: flex;
        flex-direction: column;
        padding: 0 20px;
        cursor: pointer;
        background-color: #FFF;
        border-radius: var(--skio-group-border-radius, 0);
      }

      .group-topline {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 8px;
        padding: 22px 0 16px 0;
      }

      .subscription-group-container .group-title {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .group-container--selected .group-topline {
        font-weight: 700;
      }

      :host([layout='horizontal']) .group-topline {
        flex-direction: column;
        text-align: center;
      }

      .skio-radio__container {
        display: flex;
        color: var(--skio-group-border-selected-color, #333);
      }

      .skio-radio {
        transform-origin: center;
        opacity: 0;
        transform: scale(0);
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-label:hover .skio-radio {
        opacity: 0.75;
        transform: scale(1);
      }

      .group-container--selected .group-label .skio-radio {
        opacity: 1;
        transform: scale(1);
      }

      .skio-price {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        line-height: 1;
        vertical-align: middle;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 137.5%;
        text-transform: uppercase;
        color: #242424;
      }

      .group-topline .skio-price {
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: 22px;
        letter-spacing: -0.8px;
        text-transform: uppercase;
        margin-bottom: 0;
        margin-left: auto;
      }

      .skio-price s {
        font-size: 16px;
        font-weight: 400;
        color: #737373;
      }

      :host([layout='horizontal']) .skio-price {
        text-align: center;
        margin-right: auto;
      }

      .group-content {
        display: flex;
        flex-direction: column;
        opacity: 1;
        width: auto;
        max-height: 1000px;
        transition: max-height 0.15s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .group-content.margin-left {
        // margin-left: 25px;
      }

      .group-content hr {
        border: 1px solid #f0f0f0;
      }

      .group-content p {
        margin: 0.5rem 0 0 0;
        font-size: 13px;
        color: #000;
      }

      :host([layout='horizontal']) .group-content {
        width: 100%;
      }

      /* Hide frequency if not selected */
      .group-container:not(.group-container--selected) .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      :host([layout='horizontal']) .group-container:not(.group-container--selected) + .group-content {
        pointer-events: none;
        opacity: 0;
        max-height: 0;
        visibility: hidden;
      }

      .group-title {
        width: 100%;
        max-width: 70%;
        line-height: normal;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 125%;
        text-transform: uppercase;
        color: #242424;
      }

      :host([layout='horizontal']) .group-title {
        max-width: 100%;
      }

      .savings {
        color: var(--skio-discount-text-color, #fff);
      }

      .savings.bubble {
        padding: 0px 8px;
        background-color: var(--skio-discount-color, #0fa573);
        border: 1px var(--skio-discount-color, #0fa573) solid;
        border-radius: 4px;
        font-size: 12px;
        color: var(--skio-discount-text-color, #fff);
        white-space: nowrap;
      }

      .special-savings.savings {
        padding: 4px 8px;
        border-radius: 112px;
        border: 1px solid #242424;
        background: #E6FDA5;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 133.333%;
        letter-spacing: -0.48px;
        text-transform: uppercase;
        color: #242424;
      }

      .special-offer-badges {
        position: absolute;
        top: 0;
        right: 20px;
        transform: translateY(-50%);
        display: flex;
        flex-wrap: wrap;
        justify-content: end;
        align-items: center;
        margin-left: auto;
        gap: 8px;
      }

      .special-deal {
        padding: 4px 8px;
        border-radius: 112px;
        background: #242424;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 114.286%;
        letter-spacing: -0.56px;
        text-transform: uppercase;
        color: #E6FDA5;
      }

      .selling-plan-dropdown {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        display: flex;
        align-items: center;
        gap: 5px;
        width: 100%;
        margin-top: 5px;
        padding: 8px 35px 8px 15px;
        background-color: rgba(138, 166, 101, .15);
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px top 50%;
        background-size: 16px;
        border: 1px solid #ccc;
        border-radius: 20px;
        font-size: 14px;
        color: var(--skio-group-text-selected-color, #000);
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .selling-plan-dropdown-label {
        font-weight: normal;
      }
      .selling-plan-dropdown-label ul {
        list-style: none;
        position: relative;
        margin: 0px 8px 15px;
        padding-left: 15px;
      }

      .selling-plan-dropdown-label ul li {
        padding-left: 6px;
        font-size: 13px;
      }

      .selling-plan-dropdown-label ul li::before {
        content: "\\2717";
        position: absolute;
        left: 0;
      }

      .subscription-group-container .selling-plan-dropdown-label ul li::before {
        content: "\\2713";
      }

      .selling-plan-dropdown-label h4 {
        margin-top: 0;
        margin-bottom: 12px;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 125%;
        text-transform: uppercase;
        color: #242424;
      }

      .selling-plan-dropdown--one {
        background-image: none;
        pointer-events: none;
        background: transparent;
      }

      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(var(--skio-button-plan-selector-width, 20px), 1fr));
        gap: 10px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      .selling-plan-buttons input[type='radio'] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border: 1px solid #ccc;
        text-align: center;
        padding: 1rem 0.5rem;
      }

      .selling-plan-buttons input[type='radio']:checked + label {
        border-color: #000;
      }

      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .group-content-additional {
        margin-top: 5px;
      }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;

        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;

        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;

        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;

        margin-top: 0;
        margin-bottom: 0;

        letter-spacing: 0;
        line-height: 1.5;

        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;

        width: 2.25em;
        height: 2.25em;

        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;

        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;

        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }
      .custom-dropdown {
        position: relative;
        display: inline-block;
        width: 100%;
        margin-bottom: 16px;
      }

      .custom-dropdown--static {
        display: flex;
        align-items: center;
        width: 100%;
        margin-bottom: 8px;
      }

      .custom-dropdown__selected-text {
        font-family: "ABC Monument Grotesk Mono";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 137.5%;
        color: #242424;
        padding: 0;
      }

      .custom-dropdown__trigger {
        border-radius: 20px;
        padding: 12px 35px 12px 16px;
        background-color: #F8FAF2;
        font-size: 15px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        border: 1px solid #242424;
        cursor: pointer;
        font-family: inherit;
        box-sizing: border-box;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 137.5%;
        color: #242424;
      }
      
      /* Modify border radius and remove bottom border when open */
      .custom-dropdown.dropdown--open .custom-dropdown__trigger {
        color: #000000;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom: none;
      }

      .custom-dropdown__menu {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 1000;
        background-color: #eaede3;
        border: 1px solid #ccc;
        border-top: none;
        border-bottom-left-radius: 20px;
        border-bottom-right-radius: 20px;
        width: 100%;
        overflow: hidden;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
        padding: 0;
        margin: 0;
        box-sizing: border-box;
      }

      .custom-dropdown__item {
        padding: 0.5em 1em;
        cursor: pointer;
        list-style: none;
      }

      .custom-dropdown__item:hover {
        background-color: #eee;
      }

      .caret {
        font-size: 10px;
        margin-left: 10px;
      }
      .selling-plan-buttons { grid-auto-rows: 1fr; }
      .selling-plan-buttons label {
        position: relative;
        border-radius: 12px;
        padding: 14px 12px;
        line-height: 1.2;
        flex-direction: column;
        gap: 6px;
      }
      .plan-badge {
        display: block;
        width: 100%;
        margin-bottom: 6px;
        padding: 4px 8px;
        font-size: 11px;
        font-weight: 600;
        background: #eee;   /* default fallback */
        color: #111;        /* default fallback */
        border: 1px solid transparent;
        border-radius: 11px 11px 0 0;
        white-space: nowrap;
      }
      .plan-main { font-weight: 600; }
      .plan-sub  { font-size: 12px; opacity: .85; }

      /* Max 3 buttons per row (wrap after 3) */
      .selling-plan-buttons {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        padding: 0.75rem 0;
        border: 0;
        font-size: 13px;
      }

      /* Make labels fill their grid cells nicely */
      .selling-plan-buttons label {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 6px;
        border: 1px solid #ccc;
        text-align: center;
        padding: 14px 9px;
        border-radius: 12px;
        min-height: 64px;
        box-sizing: border-box;
      }

      .selling-plan-buttons label:has(.plan-badge) {
        padding-top: 0;
      }

      /* Accessibility focus */
      .selling-plan-buttons input:focus-visible + label {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      /* frequency item styles */
      .selling-plan-dropdown-products {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        padding: 0 0 12px 0;
      }
      .selling-plan-dropdown-products:empty {
        display: none;
      }
      .frequency-item {
        flex: 1;
        max-width: 100px;
      }

      .frequency-item--image {
        aspect-ratio: 1 / 1;
        overflow: hidden;
        border-radius: 8px;
        margin-bottom: 8px;
        background: #FBFBFB;
        border: 1px solid #242424;
        border-radius: 12px;
      }

      .frequency-item img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .frequency-item--text {
        max-width: 120px;
        margin: 0 auto;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 128.571%;
        text-align: center;
        text-transform: uppercase;
        color: #242424;
      }

      .risk-free-trial-text {
        display: flex;
        align-items: center;
        margin-bottom: 15px;
        border-radius: 8px;
        border: 1px solid var(--Green, #000);
        background: #FFF;
        overflow: hidden;
      }

      .risk-free-trial-icon {
        padding: 5px 25px;
        border-right: 1px solid var(--Green, #000);
        background: #E6FDA5;
      }

      .risk-free-trial-icon svg {
        display: block;
      }

      .risk-free-trial-text--header {
        font-size: 16px;
        font-weight: 600;
        padding: 0 15px;
      }

      .benefit-item {
        display: flex;
        align-items: flex-start;
        gap: 4px;
        margin-bottom: 0;
        font-family: "ABC Monument Grotesk Mono";
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 22px;
      }

      .benefit-item:last-child {
        margin-bottom: 16px;
      }

      .benefit-icon svg {
        display: block;
      }
      
      @media only screen and (max-width: 989px) {
      
        .frequency-item--text {
          font-size: 10px;
        }

        .skio-price {
          flex-wrap: wrap;
        }
        
        .skio-plan-picker {
          width: calc(100% + var(--outer) + var(--outer));
          margin-left: calc(var(--outer) * -1);
        }

        .group-label {
          padding: 0 16px;
        }

        .group-topline,
        .subscription-group-container .group-title {
          align-items: flex-start;
        }

        .group-topline:has(.special-offer-badges) {
          padding-bottom: 0;
          margin-bottom: -25px;
        }
        
        .group-title > span:first-child {
          width: 100%;
        }

        .custom-dropdown__trigger {
          padding-right: 16px;
        }

        .selling-plan-dropdown-label {
          display: none;
        }

        .group-title {
          width: auto;
          font-size: 14px;
        }

        .group-topline .skio-price {
          font-size: 14px;
        }

        .skio-price s {
          font-size: 12px;
        }

        .custom-dropdown__selected-text {
          font-size: 14px;
        }
        
        .frequency-item--image {
          max-width: 36px;
          margin-left: auto;
          margin-right: auto;
        }

        .benefit-item {
          margin-bottom: 0;
          font-size: 10px;
        }

        .benefit-item:last-child {
          margin-bottom: 12px;
        }

        .risk-free-trial-text--header {
          padding: 0 0 0 10px;
          font-size: 12px;
        }

        .risk-free-trial-icon {
          padding: 5px 10px;
        }

        .risk-free-trial-icon svg {
          width: 27px;
          height: auto;
        }
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    // Setup Mode is opt-in via ?skio_debug=1 instead of turning on in every theme editor session
    this.debug = this.debug || new URLSearchParams(window.location.search).has('skio_debug')
    this.variantChanged = false
    this.customAdditionalDiscount = this.customAdditionalDiscount ?? 33.5; // Percentage
    // End - Debug Variables

    this.baseProduct = null;          // one-time product (PDP product)
    this.baseSelectedVariant = null;  // selected variant on base product
    this.subSelectedVariant = null;   // selected variant on subscription product
    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false
    this.dropdownOpen = false
    this.showCustomDropdown = false;    // Global variable to control custom dropdown visibility
    this.soldOutData = {};

    this.handleClickOutside = this.handleClickOutside.bind(this)
  }

  isSubscriptionMode() {
    // we treat "subscription mode" as: a selling plan is selected
    return !!this.selectedSellingPlan;
  }

  getBaseVariant() {
    return this.baseSelectedVariant || this.selectedVariant;
  }

  getSectionRoot() {
    return this.closest('[data-section-type="product"]');
  }

  getScopedRoot() {
    return this.getSectionRoot() || this.parentElement || document;
  }

  mapVariantToSubscription(baseVariant) {
    if (!baseVariant || !this.subProduct || !this.subProduct.variants?.length) {
      return baseVariant;
    }

    const subVariants = this.subProduct.variants;

    // 1) match by options
    let match =
      subVariants.find(v =>
        v.option1 === baseVariant.option1 &&
        v.option2 === baseVariant.option2 &&
        v.option3 === baseVariant.option3
      ) || null;

    // 2) match by SKU
    if (!match && baseVariant.sku) {
      match = subVariants.find(v => v.sku && v.sku === baseVariant.sku) || null;
    }

    // 3) fallback by index
    if (!match && this.product?.variants?.length) {
      const idx = this.product.variants.findIndex(v => v.id === baseVariant.id);
      if (idx > -1 && subVariants[idx]) match = subVariants[idx];
    }

    // 4) ultimate fallback
    return match || subVariants[0];
  }

  updatePdpTextBasedOnPurchaseOption() {
    const root = this.getSectionRoot();
    if (!root) return;

    const isSub = this.isSubscriptionMode();

    const baseTitle = root.getAttribute('data-product-title') || '';
    const baseHeading = root.getAttribute('data-product-heading') || '';

    const subTitle = root.getAttribute('data-sub-product-title') || baseTitle;
    const subHeading = root.getAttribute('data-sub-product-heading') || baseHeading;

    const newTitle = isSub ? subTitle : baseTitle;
    const newHeading = isSub ? subHeading : baseHeading;

    const selectors = [
      '.product__title',
      '.product__heading--change'
    ];

    selectors.forEach(sel => {
      root.querySelectorAll(sel).forEach(el => {
        // Prefer heading for .product__heading / .product__subheading
        if (sel === '.product__title') {
          el.innerHTML = newTitle;
        } else {
          el.innerHTML = newHeading;
        }
      });
    });

    const formWrapper = root.querySelector('[data-form-wrapper]');

    if (formWrapper) {
      this.updateFormWrapper(formWrapper, isSub ? this.subSelectedVariant : this.baseSelectedVariant);
    }

    if (isSub) {
      root.querySelectorAll('.product__block--price').forEach(el => {
        el?.classList.add('product__block--subscription-price');
      });
    } else {
      root.querySelectorAll('.product__block--price').forEach(el => {
        el?.classList.remove('product__block--subscription-price');
      });
    }
    //   root.querySelector('.product__block--price')?.classList.add('product__block--subscription-price');
    // } else {
    //   root.querySelector('.product__block--price')?.classList.remove('product__block--subscription-price');
    // }
  }


  syncVariantAndPage() {
    if (!this.variantInput) return;

    const isSub = this.isSubscriptionMode();
    const targetVariant = isSub
      ? this.selectedVariant
      : this.getBaseVariant();

    if (!targetVariant) return;

    const current = String(this.variantInput.value || '');
    if (current === String(targetVariant.id)) return;

    // 1) update hidden variant input
    this.variantInput.value = targetVariant.id;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

    // 2) OPTIONAL: section rendering to update gallery/selectors
    // Configure these in your app block settings and pass via `options`
    const sectionId = this.options?.pdp_section_id;        // e.g. main product section id
    const updateSelector = this.options?.pdp_main_selector; // e.g. '#MainProduct-{{ section.id }}'
    console.log('this.options', this.options)

    if (sectionId && updateSelector) {
      const url = `${window.location.pathname}?section_id=${sectionId}&variant=${targetVariant.id}`;

      fetch(url)
        .then(res => res.text())
        .then(html => {
          const dom = new DOMParser().parseFromString(html, 'text/html');
          const newContent = dom.querySelector(updateSelector);
      const currentContent = this.getScopedRoot().querySelector(updateSelector) || document.querySelector(updateSelector);
          if (newContent && currentContent) {
            currentContent.replaceWith(newContent);
          }
        })
        .catch(err => this.error('Section render failed', err));
    }
  }

  /**
   * Update the form wrapper based on the target variant
   * @param {HTMLElement} formWrapper - The form wrapper element
   * @param {Object} targetVariant - The target variant
   */
  updateFormWrapper(formWrapper, targetVariant) {
    if (!formWrapper || !targetVariant) return;


    if (!targetVariant.available) {
      formWrapper.classList.add('variant--soldout');
    } else {
      formWrapper.classList.remove('variant--soldout');
      this.updateSoldOutStatus(formWrapper, targetVariant);
    }
  }

  /**
   * Update and override the sold out status of the form wrapper based on the target variant
   * @param {HTMLElement} formWrapper - The form wrapper element
   * @param {Object} targetVariant - The target variant
   */
  updateSoldOutStatus(formWrapper, targetVariant) {
    const isSub = this.isSubscriptionMode();
    const soldOutFeatureEnabled = isSub ? this.soldOutData?.soldOutFeatureEnabledSub : this.soldOutData?.soldOutFeatureEnabledOtp;
    const soldOutOptions = isSub ? this.soldOutData?.soldOutOptionsSub : this.soldOutData?.soldOutOptionsOtp;
    if (!soldOutFeatureEnabled || !Object.keys(soldOutOptions || {}).length) return;

    const isSoldOut = this.variantMatchesSoldOutGroups(targetVariant.title, soldOutOptions);

    if (isSoldOut) {
      formWrapper.classList.add('variant--soldout');
    } else {
      formWrapper.classList.remove('variant--soldout');
    }
  }

  groupSoldOutOptions(options) {
    if (!Array.isArray(options)) return {};

    return options.reduce((groups, option, index) => {
      if (!option?.option_name) return groups;

      const groupKey = option.option_group || `_legacy_${index}`;
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(option.option_name);
      return groups;
    }, {});
  }

  variantMatchesSoldOutGroups(variantTitle, groupedOptions) {
    const title = (variantTitle || '').toLowerCase();
    const groups = Object.values(groupedOptions || {});
    if (!groups.length) return false;

    return groups.every(names =>
      names.some(name => title.includes(name.toLowerCase()))
    );
  }

  async connectedCallback() {
    super.connectedCallback()

    this.moneyFormat = this.moneyFormat || (window?.theme?.moneyFormat) || '${{amount}}';
    document.addEventListener('click', this.handleClickOutside)

    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.soldOutData = this.soldOutData ? this.prepareSoldOutData(this.soldOutData) : {};

    // Fill blank block settings from the shared theme settings (fallbackOptions attribute)
    this.applyFallbackOptions()

    this.bindFormEvents()
    this.bindCartEvents()

    // NEW: mark the PDP product as base (one-time) product
    if (this.product && !this.baseProduct) {
      this.baseProduct = this.product;
    }

    this.rules = await this.fetchRules()
    await this.getRule()

    this.initSwatchDescription();
  }


  /**
   * Merge shared theme-setting values into `options` for every key the block left blank.
   */
  applyFallbackOptions() {
    if (!this.fallbackOptions || typeof this.fallbackOptions !== 'object') return;
    const merged = { ...(this.options || {}) };
    Object.entries(this.fallbackOptions).forEach(([key, value]) => {
      const current = merged[key];
      const currentBlank = current === undefined || current === null || (typeof current === 'string' && current.trim() === '');
      const valueBlank = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
      if (currentBlank && !valueBlank) merged[key] = value;
    });
    this.options = merged;
  }

  /**
   * Prepare the sold out data for the component
   * @param {Object} soldOutData - The sold out data
   * @returns {Object} The prepared sold out data with the following structure:
   * {
   *   soldOutFeatureEnabledSub: boolean,
   *   soldOutOptionsSub: { [optionGroup: string]: string[] },
   *   soldOutFeatureEnabledOtp: boolean,
   *   soldOutOptionsOtp: { [optionGroup: string]: string[] },
   * }
   */
  prepareSoldOutData(soldOutData) {
    const data = JSON.parse(soldOutData);
    const { soldOutFeatureEnabledSub = false, soldOutOptionsSub = [], soldOutFeatureEnabledOtp = false, soldOutOptionsOtp = [] } = data;

    return {
      soldOutFeatureEnabledSub: soldOutFeatureEnabledSub,
      soldOutOptionsSub: this.groupSoldOutOptions(soldOutOptionsSub),
      soldOutFeatureEnabledOtp: soldOutFeatureEnabledOtp,
      soldOutOptionsOtp: this.groupSoldOutOptions(soldOutOptionsOtp),
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback?.()
    document.removeEventListener('click', this.handleClickOutside)
    if (this._onSwatchChange) document.removeEventListener('change', this._onSwatchChange);
    if (this._onSwatchClick)  document.removeEventListener('click',  this._onSwatchClick);
    if (this._onThemeVariantChange && this.sectionRoot) this.sectionRoot.removeEventListener('theme:variant:change', this._onThemeVariantChange);
  }

  toggleDropdown(e) {
    e.stopPropagation();
    this.dropdownOpen = !this.dropdownOpen;
  }
  
  handleClickOutside = () => {
    if (this.dropdownOpen) {
      this.dropdownOpen = false;
    }
  }

  // SECTION: Element Templates
  radioTemplate() {
    return html`
      <div class="skio-radio__container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
          <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
        </svg>
      </div>
    `
  }

  sellingPlanDropdown(group) {
    return html`
      <select
        class="selling-plan-dropdown ${this.getAvailableSellingPlans(group).length == 1 ? 'selling-plan-dropdown--one' : ''}"
        @change=${e => {
          this.selectSellingPlan(e.target.value)
        }}
      >
        ${group
          ? this.getAvailableSellingPlans(group).map(
              selling_plan => html` <option ?selected=${group.selected_selling_plan.id === selling_plan.id} value=${selling_plan.id}>${selling_plan.name}</option> `
            )
          : ''}
      </select>
    `
  }

  customDropdown(group) {
    const plans = group ? this.getAvailableSellingPlans(group) : [];
    const selected = group.selected_selling_plan;

    // If showCustomDropdown is false, just display selected option as text
    if (!this.showCustomDropdown) {
      return html`
        <div class="custom-dropdown--static">
          <div class="custom-dropdown__selected-text">
            ${selected.name}
          </div>
        </div>
      `;
    }

    // Otherwise display full dropdown
    return html`
      <div class="custom-dropdown${this.dropdownOpen ? ' dropdown--open' : ''}">
        <button
          class="custom-dropdown__trigger"
          aria-haspopup="listbox"
          @click=${this.toggleDropdown.bind(this)}
        >
          ${selected.name}
          <span class="caret">
            <svg
              style="transform: ${this.dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'}"
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="8"
              viewBox="0 0 13 8"
              fill="none"
            >
              <path d="M0.353516 0.353516L6.35352 6.35352L12.3535 0.353516" stroke="#242424"/>
            </svg>
          </span>
        </button>
        ${this.dropdownOpen
          ? html`
            <ul class="custom-dropdown__menu" role="listbox">
              ${plans
                .filter(plan => plan.id !== selected.id)
                .map(plan => html`
                  <li
                    class="custom-dropdown__item"
                    role="option"
                    @click=${e => {
                      e.stopPropagation();
                      this.selectSellingPlan(plan.id);
                      this.dropdownOpen = false;
                    }}
                  >
                    ${plan.name}
                  </li>
                `)}
            </ul>            `
          : ''}
      </div>
    `;
  }
  
  sellingPlanButtons(group) {
    const plans = group ? this.getAvailableSellingPlans(group) : [];
    return html`
      <fieldset class="selling-plan-buttons" aria-label="Select subscription interval">
        ${plans.map(plan => {
          const id = `selling_plan_button-${plan.id}`;
          const checked = group.selected_selling_plan?.id === plan.id;
          const badge = this.badgeForPlan(plan);
          const perServe = this.pricePerServeForPlan(plan);

          const badgeStyles = [];
          if (badge?.background) badgeStyles.push(`background:${badge.background}`);
          if (badge?.color) badgeStyles.push(`color:${badge.color}`);
          if (badge?.borderColor) badgeStyles.push(`border:1px solid ${badge.borderColor}`);
          const styleAttr = badgeStyles.join(';');

          return html`
            <input
              type="radio"
              name="selling_plan_button"
              id="${id}"
              @change=${() => this.selectSellingPlan(plan.id)}
              ?checked=${checked}
            />
            <label for="${id}">
              ${badge?.title ? html`
                <span class="plan-badge" style="${styleAttr}">${badge.title}</span>
              ` : ''}
              <div class="plan-main">${this.planLabelForButtons(plan)}</div>
              ${perServe ? html`<div class="plan-sub">${perServe}</div>` : ''}
            </label>
          `;
        })}
      </fieldset>
    `;
  }


  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  groupContent(group) {
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector && this.options?.dropdownPosition == 'inside' ? 'margin-left' : ''}">
        ${this.options?.selector_type == 'dropdown' ? this.customDropdown(group) : ''}
        ${this.benefitsList()}
        ${this.additionalContentText('group')}
        <div class="selling-plan-dropdown-label">${this.additionalFrequencyLabel('group')}</div>
        <div class="selling-plan-dropdown-products">${this.additionalFrequencyProducts()}</div>
        ${this.riskFreeTrialText()}
      </div>
    `;
  }
  

  singlePlanContent() {
    return html`
      <div class="group-content ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector && this.options?.dropdownPosition == 'inside' ? 'margin-left' : ''}">
        <div class="selling-plan-dropdown-label">
          ${this.additionalFrequencyLabel('one-time')}
        </div>
        ${this.additionalContentText('one-time')}
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it's not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : '';
    }
  
    if (
      this.selectedVariant.selling_plan_allocations.length === 0 &&
      !this.options?.show_without_subscription
    ) {
      return this.debug ? this.setupMode() : '';
    }
  
    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return;
  
    const firstGroup = this.availableSellingPlanGroups?.[0];
    const baseVariant = this.getBaseVariant();
  
    return html`
      ${this.debug ? this.setupMode() : null}
  
      <fieldset class="skio-plan-picker" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">
          ${unsafeHTML(this.options?.legend_content)}
        </legend>
  
        ${!this.product.requires_selling_plan
          ? html`
              <div
                class="group-container ${this.selectedSellingPlanGroup == null ? 'group-container--selected' : ''} ${!this.options?.onetime_first ? 'group-container--last' : ''}"
                @click=${() => this.selectSellingPlanGroup(null)}
              >
                <div class="group-header"></div>
                <input
                  id="one-time-${this.key}"
                  class="group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="One time purchase"
                  ?checked=${!this.selectedSellingPlanGroup}
                  @change=${() => this.selectSellingPlanGroup(null)}
                />
  
                <label class="group-label" for="one-time-${this.key}">
                  <div class="group-topline">
                    ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}
  
                    <div class="group-title">
                      ${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}
                    </div>
  
                    <div class="skio-price" aria-live="polite">
                      ${this.options?.show_compare_price &&
                        baseVariant?.compare_at_price > baseVariant.price
                        ? html`<s aria-hidden="true">${this.money(baseVariant.compare_at_price)}</s>`
                        : ''}
                      ${baseVariant ? this.money(baseVariant.price) : this.money(this.selectedVariant.price)}
                    </div>
                  </div>
  
                  ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside' ? this.singlePlanContent() : ''}
                </label>
  
                ${this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath' ? this.singlePlanContent() : ''}
              </div>
            `
          : ''}
  
        <!-- NEW: show plan buttons ABOVE the subscription groups -->
        ${firstGroup && this.options?.selector_type === 'button' ? this.sellingPlanButtons(firstGroup) : ''}
  
        ${this.availableSellingPlanGroups?.length
          ? this.availableSellingPlanGroups.map(
              (group, index) => html`
                <div class="subscription-group-container group-container ${this.selectedSellingPlanGroup == group ? 'group-container--selected' : ''}">
                  <div class="group-header">BEST VALUE</div>
                  <input
                    id="group-${index}-${this.key}"
                    class="group-input"
                    name="skio-group-${this.key}"
                    type="radio"
                    value="${group.id}"
                    @change=${() => this.selectSellingPlanGroup(group)}
                    ?checked=${this.selectedSellingPlanGroup == group}
                  />
  
                  <div class="special-offer-badges">
                    <span class="special-deal">BEST VALUE</span>
                    <span class="special-savings savings ${this.options?.discount_style}">${this.planDisplayPercent(group.selected_selling_plan)}% OFF</span>
                  </div>
                  <label class="group-label" for="group-${index}-${this.key}">
                    <div class="group-topline">
                      ${this.options?.layout == 'horizontal' ? '' : this.options?.show_radio_selector ? this.radioTemplate() : ''}
  
                      <div class="group-title">
                        <span>${group.name !== 'Prepaid' && this.options?.subscription_title
                          ? this.options?.subscription_title
                          : group.name == 'Prepaid' && this.options?.prepaid_title
                          ? this.options?.prepaid_title
                          : group.name}</span>
  
                        ${this.discountText(group.selected_selling_plan)
                          ? html`<span class="savings ${this.options?.discount_style}">${this.discountText(group.selected_selling_plan)}</span>`
                          : ''}
  
                      </div>
                      <div class="skio-price" aria-live="polite">
                        <s aria-hidden="true">${this.money(this.selectedVariant.price)}</s>
                        ${this.money(this.planDisplayPrice(group.selected_selling_plan))}
                      </div>
                    </div>
  
                    ${this.options?.layout == 'vertical' && this.options?.dropdownPosition == 'inside' ? this.groupContent(group) : ''}
                  </label>
                </div>
  
                ${this.selectedSellingPlanGroup == group
                  ? (this.options?.layout == 'horizontal' || this.options?.dropdownPosition == 'underneath' ? this.groupContent(group) : '')
                  : ''}
              `
            )
          : ''}
  
        ${this.options?.show_details == true ? this.showDetails() : ''}
      </fieldset>
    `;
  }
  

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    this.sectionRoot = this.getSectionRoot()
    const resolvedFormId = this.formId || this.options?.form_id

    // First try to find form by specified form_id
    this.form = (resolvedFormId && this.sectionRoot && this.sectionRoot.querySelector(`#${resolvedFormId}`)) || (resolvedFormId ? document.querySelector(`#${resolvedFormId}`) : null)
    
    // If no form found by ID, search through parents and page
    if (!this.form) {
      this.form = this.findProductForm()
    }

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput && resolvedFormId && this.sectionRoot) this.variantInput = this.sectionRoot.querySelector(`[name="id"][form="${resolvedFormId}"]`)
    if (!this.variantInput && resolvedFormId) this.variantInput = document.querySelector(`[name="id"][form="${resolvedFormId}"]`)

    if (this.variantInput && this.product?.variants?.length) {

      // Initial base variant from the form
      const initialId = Number(this.variantInput.value);
      if (!this.baseSelectedVariant) {
        this.baseSelectedVariant =
          this.product.variants.find(v => v.id == initialId) || this.product.variants[0];
      }

      // If there's a subscription product, map to it
      if (this.subProduct) {
        this.subSelectedVariant = this.mapVariantToSubscription(this.baseSelectedVariant);
        this.selectedVariant = this.subSelectedVariant;
      } else {
        this.selectedVariant = this.baseSelectedVariant;
      }

      this.variantInput.addEventListener('change', e => {
        const newBase = this.product.variants.find(variant => variant.id == e.target.value);
        if (newBase) {
          this.baseSelectedVariant = newBase;
          this.variantChanged = true;

          if (this.subProduct) {
            this.subSelectedVariant = this.mapVariantToSubscription(newBase);
            this.selectedVariant = this.subSelectedVariant;
          } else {
            this.selectedVariant = newBase;
          }
        }
      });
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    this._onThemeVariantChange = e => {
      const variantId = e?.detail?.variant?.id;
      if (!variantId) return;

      const baseVar = this.product?.variants?.find(x => x.id == variantId);
      if (!baseVar) {
        this.log('Unable to find variant with id: ', variantId);
        return;
      }

      this.baseSelectedVariant = baseVar;
      if (this.subProduct) {
        this.subSelectedVariant = this.mapVariantToSubscription(baseVar);
        this.selectedVariant = this.subSelectedVariant;
      } else {
        this.selectedVariant = baseVar;
      }

      this.variantChanged = true;
      this.requestUpdate();
    };

    if (this.sectionRoot) {
      this.sectionRoot.addEventListener('theme:variant:change', this._onThemeVariantChange);
    }

  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))
      
      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    const scope = this.sectionRoot || document;
    return Array.from(scope.querySelectorAll('form')).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
      
      // NEW: initial base product + baseSelectedVariant
      if (!this.baseProduct) this.baseProduct = this.product;

      if (!this.baseSelectedVariant && this.product.variants?.length) {
        const idFromForm = this.variantInput ? Number(this.variantInput.value) : null;
        if (idFromForm) {
          this.baseSelectedVariant = this.product.variants.find(v => v.id == idFromForm) || this.product.variants[0];
        } else {
          this.baseSelectedVariant = this.product.variants[0];
        }
      }

      // If we have a subscription product, map to its variant
      if (this.subProduct && !this.subSelectedVariant && this.baseSelectedVariant) {
        this.subSelectedVariant = this.mapVariantToSubscription(this.baseSelectedVariant);
        this.selectedVariant = this.subSelectedVariant;
      } else if (!this.selectedVariant && this.baseSelectedVariant) {
        this.selectedVariant = this.baseSelectedVariant;
      }
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const planSourceProduct = this.subProduct || this.product;

      const filteredSellingPlanGroups = planSourceProduct.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' &&
          !selling_plan_group.name.toLowerCase().includes('hidden-') &&
          !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // TODO: Select proper group depending on what selling plan is selected

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()
    if (changed.has('selectedVariant') || changed.has('selectedSellingPlan') || changed.has('rule')) {
    } else {
      // safe to run anyway (cheap)
    }

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
    this.updatePdpTextBasedOnPurchaseOption();
  }

  updateExternalElements() {
    this.getScopedRoot().querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.price(this.selectedSellingPlan)
    })

    this.getScopedRoot().querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

  updateExternalPrice() {
    if (!this.options?.external_price_selector) return;

    const scopedMatches = this.getScopedRoot().querySelectorAll(this.options.external_price_selector);
    const nodes = scopedMatches.length ? scopedMatches : document.querySelectorAll(this.options.external_price_selector);

    nodes.forEach(el => {
      this.selectedSellingPlan ? (el.innerHTML = this.price(this.selectedSellingPlan)) : (el.innerHTML = this.money(this.selectedVariant.price))
    })
  }

  additionalFrequencyLabel(planType = 'group') {
    const labelKey = planType === 'group' ? 'additional_frequency_label' : 'additional_onetime_label';
    const label = this.options?.[labelKey];
    if (!label || label == '') return;
    return unsafeHTML(label);
  }

  additionalFrequencyProducts() {
    const items = [];

    // Slot 5 exists only on the block (no theme-settings fallback); blank slots are skipped.
    for (let i = 1; i <= 5; i++) {
      const img  = this.options?.[`additional_frequency_item_img_${i}`];
      const text = this.options?.[`additional_frequency_item_img_text_${i}`];

      if (!img || !text) continue;

      items.push(html`
        <div class="frequency-item">
          <div class="frequency-item--image">
            <img src="${img}" alt="${text}" />
          </div>
          <div class="frequency-item--text">${text}</div>
        </div>
      `);
    }

    return items.length ? items : '';
  }
  
  riskFreeTrialText() {
    const text = (this.options?.risk_free_text || '').trim();
    if (!text) return '';
    return html`
      <div class="risk-free-trial-text">
        <div class="risk-free-trial-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="33" height="30" viewBox="0 0 33 30" fill="none">
            <path d="M14.9199 4.02246C15.8853 3.32578 17.183 3.32592 18.1484 4.02246H18.1494C19.5985 5.06749 22.5687 6.90558 25.9834 7.25684C26.86 7.34676 27.5192 8.05109 27.5664 8.88086L27.5674 9.04785C27.0542 20.8951 19.5389 25.3023 17.2832 26.3623C17.0446 26.4741 16.7891 26.5302 16.5352 26.5303C16.2805 26.5303 16.0248 26.4751 15.7871 26.3633H15.7861C13.5307 25.3035 6.0152 20.8956 5.50195 9.04785C5.46309 8.14781 6.15026 7.35294 7.08496 7.25684H7.08594C10.0737 6.94941 12.7205 5.50379 14.3066 4.44727L14.9199 4.02246Z" stroke="black"/>
            <path d="M12.2574 14.691L14.8842 17.3176C15.2185 17.6518 15.7605 17.6518 16.0947 17.3175L21.522 11.8899" stroke="black" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="risk-free-trial-text--header">${text}</div>
      </div>
    `
  }

  benefitsList() {
    const rawText = (this.options?.benefit_change_pause_text || '').trim();
    if (!rawText) return '';

    const lines = rawText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    if (!lines.length) return '';

    return html`
        <div class="benefits-wrapper">
          <div class="benefits-list">
            ${lines.map(text => html`
              <div class="benefit-item">
                <div class="benefit-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect width="20" height="20" fill="white"/>
                    <path d="M5.09636 10.388L8.27351 13.5649C8.67785 13.9692 9.33339 13.9692 9.7377 13.5648L16.3021 7" stroke="#8BA665" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="benefit-text">${text}</div>
              </div>
            `)}
          </div>
        </div>
      `
  }

  additionalContentText(planType = 'group') {
    const contentKey = planType === 'group' ? 'additional_subscription_content' : 'additional_onetime_content';

    const content = this.options?.[contentKey];
    if (!content || content == '') return;

    const discount = this.options?.discount_format === 'absolute' ? this.money(this.discount(this.selectedSellingPlan).absolute) : this.discount(this.selectedSellingPlan).percent + '%';

    const futureAdjustments = this.postCheckoutDiscountsText(this.selectedSellingPlan) || '';

    return html`
      <div class="group-content-additional">
        ${unsafeHTML(
          content.replaceAll('[discount]', discount).replaceAll('[future_price_adjustments]', futureAdjustments)
        )}
      </div>
    `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount || !this.options?.discount_text) {
      return '' // Return empty if any invalid condition is met
    } else {
      return unsafeHTML(
        this.options?.discount_text
          .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
          .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
      )
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    // let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))
    const planSourceProduct = this.subProduct || this.product;
    let sellingPlanGroup = planSourceProduct.selling_plan_groups.find(group =>
      group.selling_plans.some(plan => plan.id == selling_plan.id)
    )


    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  // First-order subscription price, taken from Shopify's selling plan allocation so it matches checkout.
  planDisplayPrice(plan) {
    const allocation = this.selectedVariant?.selling_plan_allocations?.find(
      (item) => item.selling_plan_id == plan?.id
    );
    return allocation ? allocation.price : this.price(plan, false);
  }

  planDisplayPercent(plan) {
    const price = this.selectedVariant?.price;
    if (!price) return 0;
    return Math.round((1 - this.planDisplayPrice(plan) / price) * 100);
  }

  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  // !SECTION: Discount Functions
  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  computeEffectivePriceFor(baseCents) {
    let effective = baseCents;

    // Apply selling plan adjustment if any
    const sp = this.selectedSellingPlan;
    const adj = sp?.price_adjustments?.[0];
    if (adj) {
      switch (adj.value_type) {
        case 'percentage':
          effective = baseCents - Math.round(baseCents * (adj.value / 100));
          break;
        case 'fixed_amount':
          effective = baseCents - adj.value;
          break;
        case 'price':
          // Selling plan sets an absolute price
          effective = adj.value;
          break;
        default:
          // leave as base
          break;
      }
    }

    if (this.rule?.discountAmount) {
      effective -= Math.round(baseCents * (this.rule.discountAmount / 100));
    }

    // Clamp to >= 0 and integer
    return Math.max(0, Math.round(effective));
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }

    this.syncVariantAndPage();
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.selectedSellingPlan) {
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
      window.history.replaceState({}, '', url.href)
    } else {
      url.searchParams.delete('selling_plan')
      window.history.replaceState({}, '', url.href)
    }
  }

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const isSub = this.isSubscriptionMode();
    const targetVariant = isSub
      ? this.selectedVariant
      : this.getBaseVariant();

    const items = [
      {
        id: targetVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]
        this.baseProduct = this.baseProduct || product;
        this.baseSelectedVariant = this.baseSelectedVariant || this.selectedVariant;
        return product
      })
  }
  // !SECTION: Additional Functionality

  initSwatchDescription() {
    // 1) Parse global color map
    let colors = {};
    try {
      const mapEl = this.getScopedRoot().querySelector('script[type="application/json"][id^="color-map-"]') || document.querySelector('script[type="application/json"][id^="color-map-"]');
      if (mapEl) {
        const json = JSON.parse(mapEl.textContent || '{}');
        colors = json.colors || {};
      }
    } catch (e) {
      this.log('Color map JSON parse error', e);
    }
    this._swatchColors = colors;
  
    // 2) Resolve / create placeholder
    const selector = this.options?.swatch_desc_selector || '#pdp-swatch-desc';
    let descEl = this.getScopedRoot().querySelector(selector) || document.querySelector(selector);
  
    if (!descEl) {
      // Try to auto-insert right under the Color/Flavor selector on the PDP
      const legendEl = Array.from(this.getScopedRoot().querySelectorAll('.radio__legend__option-name'))
        .find(el => /^(color|colour|flavor|flavour)$/i.test(el.textContent.trim()));
      const hostWrap = legendEl?.closest('.selector-wrapper') || legendEl?.closest('.radio__fieldset');
  
      if (hostWrap) {
        descEl = document.createElement('div');
        descEl.id = selector.replace('#', '');
        descEl.className = '';
        hostWrap.appendChild(descEl);
      }
    }
  
    // If we still don't have a container, stop quietly.
    if (!descEl) return;
  
    this._swatchDescEl = descEl;
  
    // 3) Helpers
    const handleize = s => (s || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  
    this._updateSwatchDesc = (value) => {
      const key = handleize(value);
      const entry = this._swatchColors?.[key];
      if (entry && entry.description) {
        // Richtext from Shopify admin (safe in this context)
        this._swatchDescEl.innerHTML = entry.description;
        this._swatchDescEl.style.display = '';
      } else {
        this._swatchDescEl.innerHTML = '';
        this._swatchDescEl.style.display = 'none';
      }
    };
  
    // 4) Wire listeners (native theme radios)
    const radioSel = 'input[type="radio"][data-single-option-selector]';
    this._onSwatchChange = (e) => {
      const el = e.target;
      if (!el.matches?.(radioSel)) return;
      const n = (el.name || '').toLowerCase();
      if (n.includes('[color]') || n.includes('[colour]') || n.includes('[flavor]') || n.includes('[flavour]')) {
        if (el.checked) this._updateSwatchDesc(el.value);
      }
    };
    document.addEventListener('change', this._onSwatchChange);
  
    // 5) Wire listeners (third-party swatch apps, e.g. Swatch King)
    this._onSwatchClick = (e) => {
      const t = e.target.closest?.('[data-swatch],[data-value],.swatch__link,.swatch-selector');
      if (!t) return;
      const v = t.getAttribute('data-swatch') || t.getAttribute('data-value') || t.getAttribute('data-tooltip');
      if (v) this._updateSwatchDesc(v);
    };
    document.addEventListener('click', this._onSwatchClick);
  
    // 6) Initial paint
    const checked = Array.from(this.getScopedRoot().querySelectorAll(radioSel)).find(r => {
      const n = (r.name || '').toLowerCase();
      return r.checked && (n.includes('[color]') || n.includes('[colour]') || n.includes('[flavor]') || n.includes('[flavour]'));
    });
    if (checked) this._updateSwatchDesc(checked.value);
  }

  // turn "Delivered every 30 days" → "30-days"
  planKeyFromName(name = '') {
    const m = String(name).toLowerCase().match(/(\d+)\s*(day|week|month|year)/);
    if (!m) return String(name).toLowerCase().replace(/[^a-z0-9]+/g,'-');
    const n = m[1], unit = m[2];
    const key = unit.endsWith('y') ? `${n}-day` : `${n}-${unit}`;
    return `${key}s`; // → "30-days"
  }

  // read a badge text from global map
  badgeForPlan(plan) {
    try {
      const map = (window && window.subscriptionBadges) || {};
      const raw = map[this.planKeyFromName(plan?.name)];
      if (!raw) return null;
  
      // Back-compat: allow simple strings like "Best Value"
      if (typeof raw === 'string') {
        return { title: raw, color: undefined, background: undefined, borderColor: undefined };
      }
      // Object form: { title, color, background, borderColor? }
      return {
        title: raw.title ?? '',
        color: raw.color,
        background: raw.background,
        borderColor: raw.borderColor || raw.border, // support either key
      };
    } catch { return null; }
  }

  // price after the plan’s first price_adjustment (still in cents)
  computeEffectivePriceForPlan(plan, baseCents) {
    let effective = baseCents;
    const adj = plan?.price_adjustments?.[0];
    if (adj) {
      switch (adj.value_type) {
        case 'percentage':   effective = baseCents - Math.round(baseCents * (adj.value / 100)); break;
        case 'fixed_amount': effective = baseCents - adj.value; break;
        case 'price':        effective = adj.value; break;
      }
    }
    if (this.rule?.discountAmount) {
      effective -= Math.round(baseCents * (this.rule.discountAmount / 100));
    }
    return Math.max(0, Math.round(effective));
  }

  // per-serve (string, already formatted) with preview discount applied
  pricePerServeForPlan(plan) {
    // how many servings? prefer explicit option, else try variant metafield-ish fallbacks
    const servings =
      Number(this.options?.servings) ||
      Number(this.options?.servings_per_unit) ||
      Number(this.selectedVariant?.servings) || 0;

    if (!servings) return ''; // nothing to show

    const base = this.selectedVariant?.price || 0;
    let cents = this.computeEffectivePriceForPlan(plan, base);

    // preview discount ONLY for display
    if (this.customAdditionalDiscount > 0) {
      cents = Math.max(0, Math.round(cents * (1 - this.customAdditionalDiscount / 100)));
    }

    const perServe = Math.max(0, Math.round(cents / servings));
    return `${this.money(perServe)} / serving`;
  }

  // Detect the active presentment currency as best we can
  activeCurrency() {
    return (
      window?.Shopify?.currency?.active ||
      document.querySelector('meta[property="og:price:currency"]')?.content ||
      this.options?.currency ||
      'USD'
    );
  }

  // Use Intl so the symbol/decimals follow the active currency
  formatMoneyLocalized(cents) {
    try {
      const currency = this.activeCurrency();
      const locale =
        document.documentElement.lang ||
        navigator.language ||
        'en-US';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      // fallback to theme’s formatter
      return this.money(cents);
    }
  }

  // Try to figure out servings count
  // Find option index by name (case-insensitive). Works with Shopify's
  // product.options array which may be strings or {name,...} objects.
  optionIndexByName(name) {
    const opts = this.product?.options || [];
    const target = String(name).toLowerCase();
    return opts.findIndex(o => {
      const n = (typeof o === 'string' ? o : (o?.name || '')).toLowerCase().trim();
      return n === target;
    });
  }

  // === REPLACE your getServingsCount() with this ===
  getServingsCount() {
    // 1) If the product has an option called "Quantity", use that.
    //    Example variant values: "30", "30 Servings", "60 ct", etc.
    const qIdx = this.optionIndexByName('Quantity');
    if (qIdx > -1) {
      const raw = this.selectedVariant?.[`option${qIdx + 1}`];
      const num = Number(String(raw || '').match(/\d+/)?.[0] || '');
      if (num > 0) return num;
    }

    // 2) Optional: also support an option named "Servings" if present.
    const sIdx = this.optionIndexByName('Servings');
    if (sIdx > -1) {
      const raw = this.selectedVariant?.[`option${sIdx + 1}`];
      const num = Number(String(raw || '').match(/\d+/)?.[0] || '');
      if (num > 0) return num;
    }

    // 3) Fallbacks (keep these so older products still work)
    const fromTitle = () => {
      const t = this.selectedVariant?.title || '';
      const m = t.match(/(\d+)\s*serv/i);
      return m ? Number(m[1]) : 0;
    };

    return (
      Number(this.options?.servings) ||
      Number(this.options?.servings_per_unit) ||
      Number(this.selectedVariant?.servings) ||
      fromTitle() ||
      0
    );
  }


  // Cents for the first order AFTER the plan + rule discounts
  _perServeCents(plan) {
    const servings = this.getServingsCount();
    if (!servings) return null;

    // this.price(plan, false) already mirrors your charge logic
    let cents = this.price(plan, /* formatted */ false);

    // preview-only extra discount (doesn't change cart)
    if (this.customAdditionalDiscount > 0) {
      cents = Math.max(0, Math.round(cents * (1 - this.customAdditionalDiscount / 100)));
    }
    return Math.max(0, Math.round(cents / servings));
  }

  // Public: formatted string "€0.99 / serving"
  pricePerServeForPlan(plan) {
    const per = this._perServeCents(plan);
    if (per == null) return '';
    const unit = this.options?.serving_label || 'serving';
    return `${this.formatMoneyLocalized(per)} / ${unit}`;
  }

  planLabelForButtons(plan) {
    const name = plan?.name || '';
    const m = name.toLowerCase().match(/(\d+)\s*day/); // finds "30 day(s)", "60 day(s)", etc.
    if (m) {
      const days = Number(m[1]);
      if (days === 30) return '1 month';
      if (days === 60) return '2 months';
      if (days === 90) return '3 months';
    }
    return name; // fallback: leave as-is
  }

}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker
